import { useCallback } from 'react'

import type {
  DepositFlowCallbacks,
  DepositFlowConfig,
} from '../flow/depositFlowConfig'
import { useFunkitFlow } from '../flow/FunkitFlowProvider'
import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from '../flow/withdrawalFlowConfig'

/** Lifecycle callbacks for both managed flows (overlapping keys are identical). */
export type FunkitCheckoutCallbacks = DepositFlowCallbacks &
  WithdrawalFlowCallbacks

/**
 * The connect-rn entry hook, mirroring `@funkit/connect`'s `useFunkitCheckout`.
 * Returns imperative `begin*` calls that open the SDK's managed modal flow — the
 * host renders no screens itself.
 *
 * `beginDeposit(config)` opens the deposit flow (Add money → Transfer/UDA →
 * Choose chain / Supported tokens / QR, or the amount-first fiat surface). It
 * resolves once the flow is on screen; a config that cannot open one throws a
 * `misconfigured` `FunkitMisconfiguredError` at the call, which `await` also
 * surfaces.
 * `beginWithdrawal(config)` opens the fiat-offramp withdrawal flow (payout
 * methods → Swapped sell embed).
 * `beginCheckout` will be added here as that flow lands, matching the web
 * surface.
 */
export function useFunkitCheckout(callbacks: FunkitCheckoutCallbacks = {}) {
  const { beginDeposit: openDepositFlow, beginWithdrawal: openWithdrawalFlow } =
    useFunkitFlow()

  const beginDeposit = useCallback(
    (config: DepositFlowConfig) => openDepositFlow(config, callbacks),
    [openDepositFlow, callbacks],
  )

  const beginWithdrawal = useCallback(
    (config: WithdrawalFlowConfig) => openWithdrawalFlow(config, callbacks),
    [openWithdrawalFlow, callbacks],
  )

  return { beginDeposit, beginWithdrawal }
}
